import React from 'react';
import { Routes, Route, NavLink, Navigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, Building2, Plug, BarChart3, ShieldCheck, Lock, ScrollText, UserCog, Inbox, ArrowLeft, LogOut, Wallet, CreditCard, Package, Receipt, MessageSquareText, DatabaseBackup, Store, MessageCircle, Megaphone, SlidersHorizontal } from 'lucide-react';
import SuperAdminDashboard from '@/pages/superadmin/SuperAdminDashboard';
import TenantsPage from '@/pages/superadmin/TenantsPage';
import GlobalIntegrationsPage from '@/pages/superadmin/GlobalIntegrationsPage';
import UsageCostPage from '@/pages/superadmin/UsageCostPage';
import AiBudgetAdminPage from '@/pages/superadmin/AiBudgetAdminPage';
import PaymentsPage from '@/pages/superadmin/PaymentsPage';
import ModulesAdminPage from '@/pages/superadmin/ModulesAdminPage';
import BillingPage from '@/pages/superadmin/BillingPage';
import WhatsAppTemplatesPage from '@/pages/superadmin/WhatsAppTemplatesPage';
import WhatsAppBillingPage from '@/pages/superadmin/WhatsAppBillingPage';
import SecurityPage from '@/pages/superadmin/SecurityPage';
import AuditPage from '@/pages/superadmin/AuditPage';
import SupportLoginPage from '@/pages/superadmin/SupportLoginPage';
import InboxPage from '@/pages/superadmin/InboxPage';
import BackupPage from '@/pages/superadmin/BackupPage';
import BranchesPage from '@/pages/superadmin/BranchesPage';
import BranchModulesPage from '@/pages/superadmin/BranchModulesPage';
import SalesGeneratorPage from '@/pages/superadmin/SalesGeneratorPage';

const NAV = [
  { to: '/superadmin', end: true, icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/superadmin/tenants', icon: Building2, label: 'Mandanten' },
  { to: '/superadmin/branches', icon: Store, label: 'Branchen' },
  { to: '/superadmin/branchen-module', icon: SlidersHorizontal, label: 'Branchen-Module' },
  { to: '/superadmin/sales', icon: Megaphone, label: 'Verkauf' },
  { to: '/superadmin/integrations', icon: Plug, label: 'Globale Integrationen' },
  { to: '/superadmin/usage', icon: BarChart3, label: 'Kosten & Nutzung' },
  { to: '/superadmin/ai-budget', icon: Wallet, label: 'KI-Budget' },
  { to: '/superadmin/payments', icon: CreditCard, label: 'Zahlungen' },
  { to: '/superadmin/modules', icon: Package, label: 'Software-Abo' },
  { to: '/superadmin/billing', icon: Receipt, label: 'Abrechnung' },
  { to: '/superadmin/whatsapp-templates', icon: MessageSquareText, label: 'WhatsApp-Vorlagen' },
  { to: '/superadmin/whatsapp-billing', icon: MessageCircle, label: 'WhatsApp-Gebühren' },
  { to: '/superadmin/backup', icon: DatabaseBackup, label: 'Backup' },
  { to: '/superadmin/support', icon: UserCog, label: 'Support-Login' },
  { to: '/superadmin/inbox', icon: Inbox, label: 'Support-Posteingang' },
  { to: '/superadmin/security', icon: Lock, label: 'Sicherheit' },
  { to: '/superadmin/audit', icon: ScrollText, label: 'Audit-Log' },
];

export default function SuperAdminApp() {
  const { user, logout } = useAuth();

  // Nur Superadmin darf in den Plattform-Bereich
  if (user && user.role !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r border-slate-200 bg-white/80 backdrop-blur">
        <div className="flex items-center gap-2.5 px-6 py-6">
          <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-slate-900 shadow-lg shadow-indigo-900/40">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">Plattform</div>
            <div className="text-[11px] text-slate-500">Super-Admin</div>
          </div>
        </div>

        <nav className="flex-1 space-y-1 px-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`sa-nav-${item.label.toLowerCase().replace(/[\s&]+/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? 'bg-indigo-500/15 text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`
              }
            >
              <item.icon className="h-4.5 w-4.5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="space-y-1 border-t border-slate-200 p-3">
          <Link to="/" data-testid="sa-back-to-admin" className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            <ArrowLeft className="h-4.5 w-4.5" /> Zurück zum Studio
          </Link>
          <button onClick={logout} data-testid="sa-logout" className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            <LogOut className="h-4.5 w-4.5" /> Abmelden
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="pl-64">
        <div className="mx-auto max-w-6xl px-8 py-10">
          <Routes>
            <Route index element={<SuperAdminDashboard />} />
            <Route path="tenants" element={<TenantsPage />} />
            <Route path="branches" element={<BranchesPage />} />
            <Route path="branchen-module" element={<BranchModulesPage />} />
            <Route path="sales" element={<SalesGeneratorPage />} />
            <Route path="integrations" element={<GlobalIntegrationsPage />} />
            <Route path="usage" element={<UsageCostPage />} />
            <Route path="ai-budget" element={<AiBudgetAdminPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="modules" element={<ModulesAdminPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="whatsapp-templates" element={<WhatsAppTemplatesPage />} />
            <Route path="whatsapp-billing" element={<WhatsAppBillingPage />} />
            <Route path="backup" element={<BackupPage />} />
            <Route path="support" element={<SupportLoginPage />} />
            <Route path="inbox" element={<InboxPage />} />
            <Route path="security" element={<SecurityPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="*" element={<Navigate to="/superadmin" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
